import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useASIItemMaster from '../../../../hooks/useASIItemMaster';
import { showLoading } from 'react-global-loading';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { API_URL } from '../../../../Constant';
import messages from '../../../../assets/Messages.json';
import Remarks from './Remarks';
import Remarks__list from './Remarks__list';
import { markPageAsSaved, PAGE_NAMES, refreshBalanceSheetValidation } from '../../../../Components/BalanceSheet/BalanceSheetHeader';
import { filterRemarksList } from '../../../../Custom/Custom';

function ProfitAndLossAccount() {
  const navigate = useNavigate();

  // Refs to prevent multiple API calls
  const dataLoadedRef = useRef(false);
  const isLoadingRef = useRef(false);
  const apiCallMadeRef = useRef(false);
  const saveInProgressRef = useRef(false); // Prevent multiple save calls

  // Use the custom hook for Balance Sheet Block Item data
  const {
    blockItemData,
    filteredBlockItems,
    isLoadingBlockItems,
    debouncedBlockItemSearch,
    loadMoreBlockItemData,
    hasMoreBlockItems,
    isLoadingMoreBlockItems,
  } = useASIItemMaster();

  const [headerData, setHeaderData] = useState({
    despatchSlNo: '',
    permanentSlNo: '',
    companyName: '',
    dslNo: '',
    surveyYear: ''
  });

  const [formData, setFormData] = useState({
    items: Array(100).fill(''),
    otherItems: Array(100).fill(''),
    closingData: Array(100).fill(''),
    status: Array(100).fill('')
  });

  // State for Remarks section
  const [remarksData, setRemarksData] = useState({
    remarksBlock: 'ALL', // Items dropdown (block)
    remarksItems: '', // Columns dropdown (Columns)
    remarks: '' // Remarks textarea (remarks)
  });

  // State for Remarks List
  const [remarksList, setRemarksList] = useState([]);

  const [visibleRows, setVisibleRows] = useState(1);

  // State for dropdown management
  const [dropdownStates, setDropdownStates] = useState({});
  const [searchTerms, setSearchTerms] = useState({});
  const [selectedIndex, setSelectedIndex] = useState({}); // Track selected index for keyboard navigation
  const dropdownItemRefs = useRef({}); // Refs for dropdown items
  const justOpenedRef = useRef({}); // Track if dropdown was just opened to prevent immediate toggle

  // State for Profit & Loss specific items
  const [profitLossItems, setProfitLossItems] = useState([]);
  const [filteredProfitLossItems, setFilteredProfitLossItems] = useState([]);
  const [isLoadingProfitLossItems, setIsLoadingProfitLossItems] = useState(false);

  // Authentication configuration
  const getAuthConfig = () => {
    let token = localStorage.getItem('jwt');

    if (!token) {
      token = localStorage.getItem('token')
      localStorage.getItem('authToken')
      localStorage.getItem('accessToken')
      localStorage.getItem('userToken');
    }

    console.log('Raw token from localStorage:', token);

    if (!token) {
      console.error('No authentication token found');
      toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
      return {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      };
    }

    // Clean the token (remove any extra spaces or quotes)
    const cleanToken = token.replace(/['"]/g, '').trim();
    console.log('Cleaned token:', cleanToken);

    return {
      headers: {
        'Authorization': `Bearer ${cleanToken}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    };
  };

  // Reset refs on component mount to ensure fresh API call
  useEffect(() => {
    // Reset all refs when component mounts
    dataLoadedRef.current = false;
    isLoadingRef.current = false;
    apiCallMadeRef.current = false;
    console.log('🔄 ProfitAndLossAccount Component mounted - Refs reset for fresh API call');
  }, []);

  // Fetch header data from localStorage when component mounts
  useEffect(() => {
    const fetchHeaderData = () => {
      try {
        const balanceSheetData = localStorage.getItem('balanceSheetFormData');

        if (balanceSheetData) {
          const parsedData = JSON.parse(balanceSheetData);

          setHeaderData({
            despatchSlNo: parsedData.dslNo || '',
            permanentSlNo: parsedData.pslNo || '',
            companyName: parsedData.nameUnit || '',
            dslNo: parsedData.dslNo || '',
            surveyYear: parsedData.year || ''
          });
        } else {
          console.log('No balanceSheet data found in localStorage');
          setHeaderData({
            despatchSlNo: 'DSL No. not set',
            permanentSlNo: 'PSL No. not set',
            companyName: 'Company name not set',
            dslNo: '',
            surveyYear: ''
          });
        }
      } catch (error) {
        console.error('Error fetching data from localStorage:', error);
        setHeaderData({
          despatchSlNo: 'Error loading data',
          permanentSlNo: 'Error loading data',
          companyName: 'Error loading data',
          dslNo: '',
          surveyYear: ''
        });
      }
    };

    fetchHeaderData();
  }, []);

  // Consolidated useEffect for loading existing data - prevents multiple API calls
  useEffect(() => {
    // Check if all conditions are met and API hasn't been called yet
    const shouldLoadData = !dataLoadedRef.current &&
      !isLoadingRef.current &&
      !apiCallMadeRef.current &&
      headerData.despatchSlNo &&
      headerData.despatchSlNo !== 'DSL No. not set' &&
      headerData.despatchSlNo !== 'Error loading data' &&
      headerData.surveyYear &&
      headerData.surveyYear.trim() !== '';

    // Debug logging
    console.log('=== ProfitAndLossAccount API Call Check ===');
    console.log('dataLoadedRef.current:', dataLoadedRef.current);
    console.log('isLoadingRef.current:', isLoadingRef.current);
    console.log('apiCallMadeRef.current:', apiCallMadeRef.current);
    console.log('headerData.despatchSlNo:', headerData.despatchSlNo);
    console.log('headerData.surveyYear:', headerData.surveyYear);
    console.log('shouldLoadData:', shouldLoadData);
    // console.log('==========================================');

    if (shouldLoadData) {
      // Only load data if token exists
      const token = localStorage.getItem('jwt') || localStorage.getItem('token');
      if (token) {
        console.log('✅ Triggering loadExistingData - all conditions met');
        console.log('DSL No:', headerData.despatchSlNo);
        console.log('Survey Year:', headerData.surveyYear);
        // Mark as called immediately to prevent duplicate calls
        apiCallMadeRef.current = true;
        // Call loadExistingData directly - it will handle its own validation
        loadExistingData();
      } else {
        console.log('❌ No token found, skipping data load');
        // Reset ref if token not found so it can retry
        apiCallMadeRef.current = false;
      }
    } else {
      console.log('❌ Conditions not met for API call');
      if (dataLoadedRef.current) console.log('  - Data already loaded');
      if (isLoadingRef.current) console.log('  - Already loading');
      if (apiCallMadeRef.current) console.log('  - API call already made');
      if (!headerData.despatchSlNo || headerData.despatchSlNo === 'DSL No. not set' || headerData.despatchSlNo === 'Error loading data') console.log('  - DSL No. not available');
      if (!headerData.surveyYear || headerData.surveyYear.trim() === '') console.log('  - Survey Year not available');
    }
  }, [headerData.despatchSlNo, headerData.surveyYear]); // Depend on headerData changes

  // Additional useEffect to ensure API is called when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!dataLoadedRef.current && !isLoadingRef.current && !apiCallMadeRef.current) {
        console.log('Fallback: Triggering loadExistingData after timeout');
        const token = localStorage.getItem('jwt') || localStorage.getItem('token');
        if (token && headerData.despatchSlNo && headerData.despatchSlNo !== 'DSL No. not set') {
          loadExistingData();
        }
      }
    }, 2000); // Wait 2 seconds

    return () => clearTimeout(timer);
  }, [headerData.despatchSlNo]); // Removed profitLossItems dependency

  // Fetch Profit & Loss items on component mount - now handled in loadExistingData
  // useEffect(() => {
  //   fetchProfitLossItems();
  // }, []); // Run only once on mount

  // Function to toggle dropdown for specific row
  const toggleDropdown = (rowIndex) => {
    setDropdownStates(prev => {
      const newState = {};
      // Close all other dropdowns and toggle current one
      const isCurrentlyOpen = prev[rowIndex] || false;
      newState[rowIndex] = !isCurrentlyOpen;
      return newState;
    });
    // Reset selected index when opening dropdown
    setSelectedIndex(prev => {
      const currentState = prev[rowIndex];
      if (currentState === undefined || currentState >= 0) {
        return { ...prev, [rowIndex]: -1 };
      }
      return prev;
    });
  };

  // Function to open dropdown (without toggle)
  const openDropdown = (rowIndex) => {
    setDropdownStates(prev => {
      // If already open, don't do anything
      if (prev[rowIndex]) {
        return prev;
      }
      const newState = {};
      // Close all other dropdowns and open current one
      newState[rowIndex] = true;
      // Mark as just opened to prevent immediate toggle
      justOpenedRef.current[rowIndex] = true;
      // Clear the flag after a short delay
      setTimeout(() => {
        justOpenedRef.current[rowIndex] = false;
      }, 300);
      return newState;
    });
    // Reset selected index when opening dropdown
    setSelectedIndex(prev => ({ ...prev, [rowIndex]: -1 }));
  };

  // Function to check if dropdown should open upward
  const shouldOpenUpward = (rowIndex) => {
    const inputElement = document.querySelector(`input[data-row="${rowIndex}"]`);
    if (!inputElement) return false;

    const rect = inputElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = 200;

    // Check if there's enough space below
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    return spaceBelow < dropdownHeight && spaceAbove > dropdownHeight;
  };

  const getDropdownPosition = (rowIndex) => {
    const inputElement = document.querySelector(`input[data-row="${rowIndex}"]`);
    if (!inputElement) return { top: '100%', bottom: 'auto' };

    const rect = inputElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = 200;

    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
      return { top: 'auto', bottom: '100%' };
    } else {
      return { top: '100%', bottom: 'auto' };
    }
  };

  // Function to get dropdown style
  const getDropdownStyle = (rowIndex) => {
    const position = getDropdownPosition(rowIndex);
    const isUpward = shouldOpenUpward(rowIndex);

    return {
      ...position,
      left: 0,
      right: 0,
      zIndex: 99999,
      maxHeight: '200px',
      overflowY: 'auto',
      overflowX: 'hidden',
      minWidth: '250px',
      width: 'max-content',
      maxWidth: '400px',
      border: '1px solid #ddd',
      boxShadow: isUpward ? '0 -4px 6px rgba(0, 0, 0, 0.1)' : '0 4px 6px rgba(0, 0, 0, 0.1)',
      position: 'absolute',
      marginTop: isUpward ? '0' : '2px',
      marginBottom: isUpward ? '2px' : '0',
      transform: 'translateY(0)',
      willChange: 'transform',
      scrollbarWidth: 'thin',
      scrollbarColor: '#ccc #f1f1f1',
      backgroundColor: 'white',
      borderRadius: '4px'
    };
  };

  // Function to close dropdown for specific row
  const closeDropdown = (rowIndex) => {
    setDropdownStates(prev => {
      const newState = { ...prev };
      newState[rowIndex] = false;
      return newState;
    });
    // Reset selected index when closing dropdown
    setSelectedIndex(prev => ({ ...prev, [rowIndex]: -1 }));
  };

  // Function to close all dropdowns
  const closeAllDropdowns = () => {
    setDropdownStates({});
    setSelectedIndex({});
  };

  // Handle search input change
  const handleSearchChange = (rowIndex, value) => {
    setSearchTerms(prev => ({
      ...prev,
      [rowIndex]: value
    }));

    // Use the hook's debounced search function
    debouncedBlockItemSearch(value);

    // Filter Profit & Loss items based on search
    if (value.trim()) {
      const filtered = profitLossItems.filter(item =>
        (item.item_name || '').toLowerCase().includes(value.toLowerCase()) ||
        (item.block_item || '').toLowerCase().includes(value.toLowerCase())
      );
      setFilteredProfitLossItems(filtered);
    } else {
      setFilteredProfitLossItems(profitLossItems);
    }
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.position-relative')) {
        closeAllDropdowns();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Removed static allItemsList - using only API data from profitLossItems

  // Handle dropdown change for items
  const handleItemChange = (index, selectedItem) => {
    const newItems = [...formData.items];
    const newStatus = [...formData.status];

    if (selectedItem) {
      newItems[index] = selectedItem.item_name || '';
      newStatus[index] = selectedItem.block_item || ''; // Direct mapping since block_item is already formatted

      // Automatically add a new row if this is the last visible row
      if (index === visibleRows - 1 && visibleRows < 100) {
        setVisibleRows(visibleRows + 1);
      }
    } else {
      newItems[index] = '';
      newStatus[index] = '';
    }

    setFormData({
      ...formData,
      items: newItems,
      status: newStatus
    });

    // Close dropdown after selection
    closeDropdown(index);
  };

  // Handle keyboard navigation in dropdown
  const handleKeyDown = (e, rowIndex, filteredItemsList) => {
    const currentSelected = selectedIndex[rowIndex] !== undefined ? selectedIndex[rowIndex] : -1;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        // Open dropdown if closed
        if (!dropdownStates[rowIndex]) {
          openDropdown(rowIndex);
        }
        // Move selection down
        if (currentSelected < filteredItemsList.length - 1) {
          const newIndex = currentSelected + 1;
          setSelectedIndex(prev => ({ ...prev, [rowIndex]: newIndex }));
          // Scroll into view
          setTimeout(() => {
            const itemRef = dropdownItemRefs.current[`${rowIndex}-${newIndex}`];
            if (itemRef) {
              itemRef.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
          }, 0);
        }
        break;

      case 'ArrowUp':
        e.preventDefault();
        // Open dropdown if closed
        if (!dropdownStates[rowIndex]) {
          openDropdown(rowIndex);
        }
        // Move selection up
        if (currentSelected > 0) {
          const newIndex = currentSelected - 1;
          setSelectedIndex(prev => ({ ...prev, [rowIndex]: newIndex }));
          // Scroll into view
          setTimeout(() => {
            const itemRef = dropdownItemRefs.current[`${rowIndex}-${newIndex}`];
            if (itemRef) {
              itemRef.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
          }, 0);
        }
        break;

      case 'Enter':
        e.preventDefault();
        if (dropdownStates[rowIndex] && currentSelected >= 0 && currentSelected < filteredItemsList.length) {
          // Select the highlighted item
          handleItemChange(rowIndex, filteredItemsList[currentSelected]);
        } else if (!dropdownStates[rowIndex]) {
          // Open dropdown if closed
          openDropdown(rowIndex);
        }
        break;

      case 'Escape':
        e.preventDefault();
        // Close dropdown
        closeDropdown(rowIndex);
        break;

      case 'Tab':
        // Close dropdown on tab
        closeDropdown(rowIndex);
        break;

      default:
        // For other keys, open dropdown if typing
        if (!dropdownStates[rowIndex] && e.key.length === 1) {
          openDropdown(rowIndex);
        }
        break;
    }
  };

  // Handle delete row functionality
  const handleDeleteSingleRow = (index) => {
    // Show confirmation before deletion
    const itemName = formData.items[index] || '';
    const confirmMessage = `Are you sure you want to delete Row ${index + 1}?${itemName ? `\n\nItem: ${itemName}` : ''}`;
    const confirmDelete = window.confirm(confirmMessage);

    if (!confirmDelete) {
      return; // User cancelled deletion
    }

    // Create new arrays with the row removed and shift remaining rows up
    const newItems = [...formData.items];
    const newOtherItems = [...formData.otherItems];
    const newClosingData = [...formData.closingData];
    const newStatus = [...formData.status];

    // Remove the row at index and shift remaining rows up
    newItems.splice(index, 1);
    newOtherItems.splice(index, 1);
    newClosingData.splice(index, 1);
    newStatus.splice(index, 1);

    // Add empty row at the end to maintain array length
    newItems.push('');
    newOtherItems.push('');
    newClosingData.push('');
    newStatus.push('');

    // Update form data
    setFormData({
      items: newItems,
      otherItems: newOtherItems,
      closingData: newClosingData,
      status: newStatus
    });

    // Update visible rows - decrease by 1 if we deleted a visible row
    if (index < visibleRows) {
      setVisibleRows(Math.max(1, visibleRows - 1));
    }

    // Close dropdowns
    closeAllDropdowns();

    toast.success(`Row ${index + 1} deleted successfully`, messages.COLOR_RED);
  };

  // Handle input changes for other fields
  const handleInputChange = (index, field, value) => {
    const newFormData = { ...formData };

    // Apply validation based on field type
    let validatedValue = value;

    // Numeric validation for closing field
    if (field === 'closingData') {
      // Allow empty string, numbers, and decimal points
      if (value === '' || value === '0' || /^\d*\.?\d*$/.test(value)) {
        // Limit to 2 decimal places
        if (value.includes('.')) {
          const parts = value.split('.');
          if (parts[1] && parts[1].length > 2) {
            validatedValue = parts[0] + '.' + parts[1].substring(0, 2);
          }
        }

        // Limit total length to 15 characters (including decimal point)
        if (validatedValue.length > 15) {
          validatedValue = validatedValue.substring(0, 15);
        }
      } else {
        // If invalid characters, keep previous value
        return;
      }
    }

    newFormData[field][index] = validatedValue;
    setFormData(newFormData);
  };

  // Function to extract block information from status field
  const getBlockInfo = (status) => {
    if (!status || status === '') {
      return { blockId: 'G', blockType: 'ASI_BLOCKG', itemCode: 1 }; // Default to Block G for Profit & Loss
    }

    // Extract block letter and item code from status (e.g., "Block-F, Item-3" -> "F" and "3")
    const blockMatch = status.match(/Block-([A-Z]),\s*Item-(\d+)/i);
    if (blockMatch) {
      const blockLetter = blockMatch[1];
      const itemCode = parseInt(blockMatch[2]);
      return {
        blockId: blockLetter,
        blockType: `ASI_BLOCK${blockLetter}`,
        itemCode: itemCode
      };
    }

    // Try alternative patterns
    const altMatch = status.match(/Block ([A-Z]),\s*Item-(\d+)/i);
    if (altMatch) {
      const blockLetter = altMatch[1];
      const itemCode = parseInt(altMatch[2]);
      return {
        blockId: blockLetter,
        blockType: `ASI_BLOCK${blockLetter}`,
        itemCode: itemCode
      };
    }

    // Try to extract just block letter if item code not found
    const blockOnlyMatch = status.match(/Block-([A-Z])/i);
    if (blockOnlyMatch) {
      const blockLetter = blockOnlyMatch[1];
      return {
        blockId: blockLetter,
        blockType: `ASI_BLOCK${blockLetter}`,
        itemCode: 1 // Default item code if not found
      };
    }

    // If no match found, return default for Profit & Loss (Block G)
    return { blockId: 'G', blockType: 'ASI_BLOCKG', itemCode: 1 };
  };

  // Fetch Profit & Loss specific items from API
  const fetchProfitLossItems = async () => {
    setIsLoadingProfitLossItems(true);

    try {
      const currentConfig = getAuthConfig();
      if (!currentConfig) {
        throw new Error('Authentication token not found');
      }

      console.log('Fetching Profit & Loss items from API...');
      const response = await axios.get(`${API_URL}api/asi/balanceSheetBlockItemsByTypes`, currentConfig);

      if (response.data && response.data.status && response.data.data) {
        const data = response.data.data;
        console.log(`Loaded ${data.length} Profit & Loss items from API`);

        // Sort data by item_name for consistent ordering
        const sortedData = data.sort((a, b) =>
          (a.item_name || '').localeCompare(b.item_name || '')
        );

        setProfitLossItems(sortedData);
        setFilteredProfitLossItems(sortedData);
      } else {
        throw new Error(response.data.message || 'Failed to load Profit & Loss items');
      }
    } catch (error) {
      console.error('Error fetching Profit & Loss items:', error);

      if (error.response?.status === 403) {
        toast.error('Access denied (403). Please check your permissions.');
      } else if (error.response?.status === 401) {
        toast.error('Unauthorized (401). Please login again.');
      } else {
        toast.error('Error loading Profit & Loss items. Please try again.');
      }
    } finally {
      setIsLoadingProfitLossItems(false);
    }
  };

  const loadExistingData = async () => {
    if (!headerData.despatchSlNo || headerData.despatchSlNo === 'DSL No. not set' || headerData.despatchSlNo === 'Error loading data' || !headerData.surveyYear || headerData.surveyYear.trim() === '') {
      console.log('❌ loadExistingData: Missing required data - DSL No. or Survey Year');
      console.log('DSL No:', headerData.despatchSlNo);
      console.log('Survey Year:', headerData.surveyYear);
      apiCallMadeRef.current = false;
      return;
    }

    if (isLoadingRef.current) {
      console.log('loadExistingData: Already loading, skipping...');
      return;
    }
    isLoadingRef.current = true;
    showLoading(true);

    try {
      const payload = {
        dsl_no: headerData.despatchSlNo,
        survey_year: headerData.surveyYear.split('-')[0]
      };

      console.log('Loading existing data with payload:', payload);

      const [blockResponse, profitLossResponse] = await Promise.all([
        axios.post(`${API_URL}api/block/get-data`, payload, getAuthConfig()),
        axios.get(`${API_URL}api/asi/balanceSheetBlockItemsByTypes`, getAuthConfig())
      ]);

      console.log('Block API Response:', blockResponse.data);
      console.log('Profit Loss Items API Response:', profitLossResponse.data);

      // Handle block data response
      if (blockResponse.data && blockResponse.data.status && blockResponse.data.data) {
        const existingData = blockResponse.data.data;
        console.log('Existing data from API:', existingData);
        console.log('Remarks from API:', blockResponse.data.remarks);

        const newFormData = {
          items: Array(100).fill(''),
          otherItems: Array(100).fill(''),
          closingData: Array(100).fill(''),
          status: Array(100).fill('')
        };

        // Handle profit loss items response
        let availableProfitLossItems = [];
        if (profitLossResponse.data && profitLossResponse.data.status && profitLossResponse.data.data) {
          const profitLossData = profitLossResponse.data.data;
          console.log(`Loaded ${profitLossData.length} Profit & Loss items from API`);

          const sortedData = profitLossData.sort((a, b) =>
            (a.item_name || '').localeCompare(b.item_name || '')
          );

          availableProfitLossItems = sortedData;
          setProfitLossItems(sortedData);
          setFilteredProfitLossItems(sortedData);
        }

        existingData.forEach((item, index) => {
          if (index < 100) {
            console.log(`Processing item ${index}:`, item);

            // Map itemName (handle both camelCase and snake_case)
            const itemName = item.itemName || item.item_name || '';

            const matchingItem = availableProfitLossItems.find(dropdownItem =>
              dropdownItem.item_name === itemName
            );

            if (matchingItem) {
              newFormData.items[index] = matchingItem.item_name || '';
              // Use blockItem from API response or from matching item
              newFormData.status[index] = item.blockItem || item.block_item || matchingItem.block_item || '';
            } else {
              newFormData.items[index] = itemName;
              // Use blockItem from API response
              newFormData.status[index] = item.blockItem || item.block_item || '';
            }

            // Map otherItem (handle both camelCase and snake_case)
            newFormData.otherItems[index] = item.otherItem || item.other_item || '';

            // Map value (handle both number and string)
            const value = item.value !== undefined && item.value !== null ? item.value : '';
            newFormData.closingData[index] = value.toString();

            console.log(`Mapped item ${index}: ${itemName} -> Status: ${newFormData.status[index]} -> Value: ${value} -> OtherItem: ${newFormData.otherItems[index]}`);
          }
        });

        let mappedRemarksList = [];
        if (blockResponse.data.remarks && Array.isArray(blockResponse.data.remarks)) {
          mappedRemarksList = blockResponse.data.remarks.map((remark, index) => {
            // Extract block letter from "BLOCK F" format
            let blockLetter = '';
            if (remark.block) {
              // Handle formats like "BLOCK F", "BLOCK-F", "F", etc.
              blockLetter = remark.block.replace(/^BLOCK\s*[-]?\s*/i, '').trim().toUpperCase();
            }

            // Format date for display
            let formattedDate = '';
            const dateValue = remark.createdDate || remark.created_date || remark.updatedDate || remark.updated_date || '';
            if (dateValue) {
              try {
                const date = new Date(dateValue);
                if (!isNaN(date.getTime())) {
                  formattedDate = date.toLocaleString('en-IN', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  });
                }
              } catch (e) {
                formattedDate = dateValue;
              }
            }

            return {
              slNo: index + 1,
              blocks: blockLetter || remark.block || '',
              columns: remark.columnsName || remark.columns_name || remark.columns || '',
              items: remark.items || remark.Items || '',
              remarks: remark.remarks || '',
              remarksCreatedOn: formattedDate || dateValue || '',
              remarksBy: remark.updatedBy || remark.updated_by || remark.createdBy || remark.created_by || '',
              role: remark.updatedBy || remark.updated_by || remark.createdBy || remark.created_by || ''
            };
          });
          setRemarksList(mappedRemarksList);
          console.log('Mapped remarks list:', mappedRemarksList);
        } else {
          setRemarksList([]);
        }

        setRemarksData({
          remarksBlock: (mappedRemarksList.length > 0) ? 'ALL' : 'SELECT_REMARKS',
          remarksItems: '',
          remarks: ''
        });

        setFormData(newFormData);

        // Set visible rows based on data
        const filledRows = existingData.length;
        setVisibleRows(Math.max(1, Math.min(100, filledRows + 1)));

        dataLoadedRef.current = true;
      }
    } catch (error) {
      console.error('Error loading existing data:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.', messages.COLOR_RED);
      } else if (error.response?.status === 403) {
        toast.error('Access denied. Please check your permissions.', messages.COLOR_RED);
      } else if (error.response?.status === 404) {
        console.log('No existing data found');
      } else {
        toast.error('Error loading data. Please try again.', messages.COLOR_RED);
      }
    } finally {
      isLoadingRef.current = false;
      // Hide global loading
      showLoading(false);
    }
  };

  // Save data to API
  const handleSaveToAPI = async () => {
    // Prevent multiple save calls
    if (saveInProgressRef.current) {
      console.log('Save already in progress, skipping...');
      return false;
    }

    if (!headerData.despatchSlNo || headerData.despatchSlNo === 'DSL No. not set') {
      toast.error('Please ensure DSL No. is available', messages.COLOR_RED);
      return false;
    }

    // Check if token exists before making API call
    let token = localStorage.getItem('jwt'); // Use JWT token first

    // Try alternative token keys if 'jwt' is not found
    if (!token) {
      token = localStorage.getItem('token') ||
        localStorage.getItem('authToken') ||
        localStorage.getItem('accessToken') ||
        localStorage.getItem('userToken');
    }

    console.log('All localStorage keys:', Object.keys(localStorage));
    console.log('Token value:', token);
    console.log('Token type:', typeof token);
    console.log('Token length:', token ? token.length : 0);

    if (!token) {
      toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
      return false;
    }

    console.log('Starting save process with token:', token ? 'Token exists' : 'No token');
    console.log('DSL No:', headerData.despatchSlNo);

    // Set save in progress flag
    saveInProgressRef.current = true;

    // Show global loading
    showLoading(true);

    try {
      const dataToSave = [];

      for (let i = 0; i < visibleRows; i++) {
        const item = {
          item_name: formData.items[i] || '',
          other_item: formData.otherItems[i] || '',
          value: formData.closingData[i] || '',
          block_item: formData.status[i] || ''
        };

        // Only add if at least one field has data
        if (item.item_name || item.value || item.other_item) {

          // Get dynamic block information from status field
          const blockInfo = getBlockInfo(item.block_item);
          console.log(`Row ${i}: Status="${item.block_item}" -> BlockId="${blockInfo.blockId}", BlockType="${blockInfo.blockType}", ItemCode="${blockInfo.itemCode}"`);

          const payload = {
            dsl_no: headerData.despatchSlNo,
            survey_year: headerData.surveyYear.split('-')[0] || '2022', // Extract year from format like "2022-23"
            block_type: blockInfo.blockType,
            item_code: blockInfo.itemCode, // Use extracted item code from BLOCK_ITEM
            asset_code: "", // Empty asset_code as per requirement
            item_name: item.item_name,
            other_item: item.other_item || '',
            BLOCK_ID: blockInfo.blockId,
            BLOCK_ITEM: item.block_item || `Block-${blockInfo.blockId},Item-${blockInfo.itemCode}`, // Format: Block-G,Item-12
            value: item.value ? parseFloat(item.value) : 0
          };

          dataToSave.push(payload);
        }
      }

      if (dataToSave.length === 0) {
        toast.warning('No data to save', messages.COLOR_RED);
        return false;
      }

      // Prepare remarks array
      const remarksArray = [];

      // Check if any remarks data exists (remarks text is mandatory, but other fields can be optional)
      const hasRemarksText = remarksData.remarks && remarksData.remarks.trim() !== '';
      const hasRemarksBlock = remarksData.remarksBlock && remarksData.remarksBlock !== 'SELECT_REMARKS' && remarksData.remarksBlock !== 'ALL';
      const hasRemarksItems = remarksData.remarksItems && remarksData.remarksItems.trim() !== '';

      if (hasRemarksText || hasRemarksBlock || hasRemarksItems) {
        // Extract block letter from remarksBlock if it contains block info, otherwise use the selected item
        let blockLetter = '';
        if (hasRemarksBlock) {
          // Check if remarksBlock contains block info (e.g., "Block-F, Item-3" -> "F")
          const blockMatch = remarksData.remarksBlock.match(/Block-([A-Z])/i);
          if (blockMatch) {
            blockLetter = blockMatch[1];
          } else {
            // If it's just an item name, try to find the block from the selected item
            const selectedItem = profitLossItems.find(item => item.item_name === remarksData.remarksBlock);
            if (selectedItem && selectedItem.block_item) {
              const itemBlockMatch = selectedItem.block_item.match(/Block-([A-Z])/i);
              if (itemBlockMatch) {
                blockLetter = itemBlockMatch[1];
              }
            }
          }
        }

        remarksArray.push({
          dsl_no: headerData.despatchSlNo,
          survey_year: headerData.surveyYear.split('-')[0] || '2022',
          block: blockLetter || '',
          columnsName: remarksData.remarksItems || '',
          items: hasRemarksBlock ? remarksData.remarksBlock : '',
          remarks: remarksData.remarks || ''
        });
      }

      // Structure payload with items and remarks arrays
      const finalPayload = {
        items: dataToSave,
        remarks: remarksArray
      };

      console.log('Sending data to API:', finalPayload);
      console.log('API URL:', `${API_URL}api/block/insert-item`);
      console.log('Dynamic block mapping applied for each row');

      // Get fresh token and create auth config
      let freshToken = localStorage.getItem('jwt'); // Use JWT token first
      if (!freshToken) {
        freshToken = localStorage.getItem('token') ||
          localStorage.getItem('authToken') ||
          localStorage.getItem('accessToken') ||
          localStorage.getItem('userToken');
      }

      console.log('Fresh token retrieved:', freshToken);

      if (!freshToken) {
        toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
        return false;
      }

      const cleanToken = freshToken.replace(/['"]/g, '').trim();
      console.log('Clean token for API:', cleanToken);

      const authConfig = {
        headers: {
          'Authorization': `Bearer ${cleanToken}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      };

      console.log('Auth headers:', authConfig);
      console.log('Token in header:', authConfig.headers?.Authorization);

      // Add request interceptor to verify token is being sent
      const requestInterceptor = axios.interceptors.request.use(
        (config) => {
          console.log('Request interceptor - Headers being sent:', config.headers);
          console.log('Request interceptor - Authorization header:', config.headers?.Authorization);
          return config;
        },
        (error) => {
          console.error('Request interceptor error:', error);
          return Promise.reject(error);
        }
      );

      const response = await axios.post(`${API_URL}api/block/insert-item`, finalPayload, authConfig);

      // Remove the interceptor after use
      axios.interceptors.request.eject(requestInterceptor);

      console.log('API Response:', response.data);

      if (response.data && response.data.status) {
        toast.success('Data saved successfully!', messages.COLOR_RED);

        // Mark page as saved for green header
        markPageAsSaved(PAGE_NAMES.PROFIT_AND_LOSS);
        // Refresh balance sheet validation to update header colors
        if (headerData.despatchSlNo && headerData.surveyYear) {
          await refreshBalanceSheetValidation(headerData.despatchSlNo, headerData.surveyYear);
        }

        return true;
      } else {
        toast.error('Failed to save data', messages.COLOR_RED);
        return false;
      }
    } catch (error) {
      console.error('Error saving data:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);

      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.', messages.COLOR_RED);
      } else if (error.response?.status === 403) {
        toast.error('Access denied. Please check your permissions or contact administrator.', messages.COLOR_RED);
        console.error('403 Error Details:', error.response?.data);
      } else if (error.response?.status === 400) {
        toast.error('Invalid data. Please check your inputs.', messages.COLOR_RED);
        console.error('400 Error Details:', error.response?.data);
      } else {
        toast.error(`Error saving data: ${error.message}`, messages.COLOR_RED);
      }
      return false;
    } finally {
      // Reset save in progress flag
      saveInProgressRef.current = false;
      // Hide global loading
      showLoading(false);
    }
  };


  // Generate table rows dynamically
  const generateTableRows = () => {
    const rows = [];
    // Use only API data from profitLossItems - no static fallback
    const currentFilteredItems = filteredProfitLossItems.length > 0 ? filteredProfitLossItems : profitLossItems;

    // Get all already selected items
    const selectedItems = new Set(formData.items.filter(item => item && item.trim() !== ''));

    for (let i = 0; i < visibleRows; i++) {
      // Filter items: remove those already selected in other rows, but keep current row's item
      const availableItems = currentFilteredItems.filter(item => {
        const isAlreadySelected = selectedItems.has(item.item_name);
        const isCurrentRowItem = formData.items[i] === item.item_name;
        // Show item if: it's not selected elsewhere OR it's the current row's selected item
        return !isAlreadySelected || isCurrentRowItem;
      });

      rows.push(
        <tr key={i}>
          <td style={{ textAlign: 'center', fontWeight: '500' }}>{i + 1}</td>
          <td>
            <div className="position-relative" style={{ position: 'relative', overflow: 'visible' }}>
              <input
                type="text"
                className="form-control"
                value={formData.items[i] || ''}
                placeholder={isLoadingProfitLossItems ? 'Loading...' : 'Select Item'}
                data-row={i}
                style={{
                  textAlign: 'center',
                  paddingRight: '30px',
                  fontSize: '12px',
                  minHeight: '32px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
                onChange={(e) => {
                  handleSearchChange(i, e.target.value);
                  // Update form data for manual input
                  const newItems = [...formData.items];
                  newItems[i] = e.target.value;
                  setFormData({ ...formData, items: newItems });
                  // Reset selected index when typing
                  setSelectedIndex(prev => ({ ...prev, [i]: -1 }));
                }}
                onFocus={(e) => {
                  // Open dropdown on focus if it's closed
                  if (!dropdownStates[i]) {
                    openDropdown(i);
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  // Only toggle if dropdown wasn't just opened by mousedown
                  if (!justOpenedRef.current[i]) {
                    toggleDropdown(i);
                  } else {
                    // Clear the flag since we handled it
                    justOpenedRef.current[i] = false;
                  }
                }}
                onMouseDown={(e) => {
                  // Open dropdown immediately on mousedown to ensure it opens on first click
                  // This prevents the need for double-click
                  if (!dropdownStates[i]) {
                    openDropdown(i);
                  }
                }}
                onKeyDown={(e) => {
                  handleKeyDown(e, i, availableItems);
                }}
                disabled={isLoadingProfitLossItems}
              />
              <i className="fa fa-chevron-down position-absolute"
                style={{
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#666',
                  pointerEvents: 'none'
                }}></i>
              {dropdownStates[i] && (
                <div
                  className="position-absolute bg-white border rounded shadow-sm custom-dropdown"
                  style={getDropdownStyle(i)}
                >
                  {availableItems.length > 0 ? (
                    <>
                      {availableItems.map((singleItem, index) => {
                        const isSelected = selectedIndex[i] === index;
                        return (
                          <div
                            key={index}
                            ref={(el) => {
                              if (el) {
                                dropdownItemRefs.current[`${i}-${index}`] = el;
                              }
                            }}
                            className="px-3 py-2 cursor-pointer"
                            style={{
                              cursor: 'pointer',
                              borderBottom: '1px solid #f0f0f0',
                              fontSize: '12px',
                              wordWrap: 'break-word',
                              whiteSpace: 'normal',
                              minHeight: '40px',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              backgroundColor: isSelected ? '#e3f2fd' : 'white',
                              outline: isSelected ? '2px solid #1976d2' : 'none',
                              outlineOffset: '-2px'
                            }}
                            onClick={() => handleItemChange(i, singleItem)}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#f8f9fa';
                              setSelectedIndex(prev => ({ ...prev, [i]: index }));
                            }}
                            onMouseLeave={(e) => {
                              if (!isSelected) {
                                e.target.style.backgroundColor = 'white';
                              }
                            }}
                            tabIndex={-1}
                          >
                            <div style={{
                              fontWeight: 'bold',
                              marginBottom: '2px',
                              wordBreak: 'break-word',
                              lineHeight: '1.3'
                            }}>
                              {singleItem.item_name}
                            </div>
                            {singleItem.block_item && (
                              <div style={{
                                color: '#666',
                                fontSize: '11px',
                                wordBreak: 'break-word',
                                lineHeight: '1.2'
                              }}>
                                {singleItem.block_item}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    <div className="px-3 py-2 text-muted" style={{ fontSize: '12px' }}>
                      {isLoadingProfitLossItems ? 'Loading items...' : 'No items available'}
                    </div>
                  )}
                </div>
              )}
            </div>
          </td>
          <td>
            <input
              type="text"
              className="form-control"
              value={formData.otherItems[i] || ''}
              onChange={(e) => handleInputChange(i, 'otherItems', e.target.value)}
              maxLength="200"
              // disabled={!formData.items[i] || formData.items[i].trim() === ''}
              disabled style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed', fontSize: '12px', minHeight: '32px' }}
            // style={{ 
            //   fontSize: '12px', 
            //   minHeight: '32px', 
            //   textAlign: 'center',
            //   cursor: (!formData.items[i] || formData.items[i].trim() === '') ? 'not-allowed' : 'text'
            // }} 
            />
          </td>
          <td><input
            type="text"
            className="form-control"
            value={formData.closingData[i]}
            onChange={(e) => handleInputChange(i, 'closingData', e.target.value)}
            maxLength="15"
            pattern="[0-9]*\.?[0-9]*"
            disabled={!formData.items[i] || formData.items[i].trim() === ''}
            style={{
              fontSize: '12px',
              minHeight: '32px',
              textAlign: 'center',
              backgroundColor: (!formData.items[i] || formData.items[i].trim() === '') ? '#f5f5f5' : '#fff',
              cursor: (!formData.items[i] || formData.items[i].trim() === '') ? 'not-allowed' : 'text'
            }}
          /></td>
          <td>
            <input
              type="text"
              className="form-control"
              value={formData.status[i]}
              disabled style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed', fontSize: '12px', minHeight: '32px' }}
            />
          </td>
          <td>
            {(i !== 0 && i !== visibleRows - 1) && (
              <a
                href="#"
                className='d-block text-center'
                onClick={(e) => {
                  e.preventDefault();
                  handleDeleteSingleRow(i);
                }}
                title={`Delete Row ${i + 1}`}
              >
                <i className="fa fa-trash"></i>
              </a>
            )}
          </td>
        </tr>
      );
    }
    return rows;
  };

  // Filter remarks list using centralized function from Custom.js
  const filteredRemarksList = useMemo(() => {
    return filterRemarksList(remarksList, remarksData);
  }, [remarksList, remarksData]);

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />
      <style>
        {`
          .custom-dropdown::-webkit-scrollbar {
            width: 6px;
          }
          .custom-dropdown::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 3px;
          }
          .custom-dropdown::-webkit-scrollbar-thumb {
            background: #ccc;
            border-radius: 3px;
          }
          .custom-dropdown::-webkit-scrollbar-thumb:hover {
            background: #999;
          }

          /* Print Styles */
          @media print {
            @page {
              size: A4 landscape;
              margin: 0.5cm;
            }

            body {
              margin: 0;
              padding: 0;
            }

            .container-fluid {
              padding: 0 !important;
              margin: 0 !important;
            }

            .card {
              margin: 0 !important;
              box-shadow: none !important;
              border: none !important;
            }

            /* Hide action buttons during print */
            .btn_orange,
            button[type="button"],
            hr {
              display: none !important;
            }

            /* Ensure table fits on page */
            table {
              width: 100% !important;
              font-size: 9px !important;
              page-break-inside: auto !important;
            }

            thead {
              display: table-header-group !important;
            }

            tbody {
              display: table-row-group !important;
            }

            tr {
              page-break-inside: avoid !important;
              page-break-after: auto !important;
            }

            th, td {
              padding: 4px 2px !important;
              border: 1px solid #000 !important;
              word-wrap: break-word !important;
              overflow-wrap: break-word !important;
            }

            th {
              font-size: 8px !important;
              font-weight: bold !important;
              background-color: #f0f0f0 !important;
            }

            td {
              font-size: 8px !important;
            }

            /* Adjust column widths for print */
            table th:nth-child(1),
            table td:nth-child(1) {
              width: 35% !important;
              min-width: 200px !important;
            }

            table th:nth-child(2),
            table td:nth-child(2) {
              width: 20% !important;
              min-width: 120px !important;
            }

            table th:nth-child(3),
            table td:nth-child(3) {
              width: 25% !important;
              min-width: 150px !important;
            }

            /* Hide dropdown elements during print */
            .fa-chevron-down,
            .position-absolute:not(.print-visible),
            .custom-dropdown {
              display: none !important;
            }

            /* Ensure inputs are readable */
            input.form-control {
              border: none !important;
              background: transparent !important;
              padding: 0 !important;
              font-size: 8px !important;
              text-align: right !important;
            }

            input.form-control:disabled {
              background: transparent !important;
            }
          }
        `}
      </style>
      <div className="container-fluid py-3">
        {/* Header Section - Direct Display */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row align-items-center">
              <div className="col-md-12 mb-3">
                <h4 className="page_heading mb-3 pb-2 border-bottom">
                  ASI Schedule 2024-25 <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>CONFIDENTIAL <i className="fa fa-lock me-2" style={{ fontSize: '20px', color: '#1976d2' }}></i></span>
                </h4>
                <h5 className="mb-0 pb-3 border-bottom"><span style={{ color: '#1976d2', fontWeight: 'bold' }}>Profit and Loss Account</span> <strong>Data for Block E, F, G, H, I, J</strong></h5>
              </div>
              <div className="row">
                <div className="col-md-8">
                  <div className="row g-3">
                    <div className="col-md-4">
                      <div style={{ fontSize: '14px', fontWeight: '500', color: '#333', marginBottom: '6px' }}>
                        <strong>DSL No.:</strong>
                      </div>
                      <div style={{ fontSize: '14px', color: '#333' }}>
                        {headerData.despatchSlNo}
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div style={{ fontSize: '14px', fontWeight: '500', color: '#333', marginBottom: '6px' }}>
                        <strong>PSL No.:</strong>
                      </div>
                      <div style={{ fontSize: '14px', color: '#333' }}>
                        {headerData.permanentSlNo}
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div style={{ fontSize: '14px', fontWeight: '500', color: '#333', marginBottom: '6px' }}>
                        <strong>Name of Company:</strong>
                      </div>
                      <div style={{ fontSize: '14px', color: '#333' }}>
                        {headerData.companyName}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table Styles */}
        <style>
          {`
            .table th {
              background-color: #f8f9fa;
              border: 1px solid #dee2e6;
              font-weight: 600;
              font-size: 14px;
              text-align: center;
              vertical-align: middle;
              padding: 8px 4px;
            }
            .table td {
              border: 1px solid #dee2e6;
              padding: 6px 4px;
              font-size: 13px;
            }
            .table input {
              border: 1px solid #e0e0e0;
              background: white;
              width: 100%;
              text-align: center;
              font-size: 13px;
              border-radius: 6px;
              padding: 8px 12px;
              transition: all 0.3s ease;
            }
            .table input:focus {
              outline: none;
              border-color: #f75226;
              transform: translateY(-1px);
            }
            .table input:disabled {
              background-color: #f8f9fa;
              border-color: #dee2e6;
              color: #6c757d;
              cursor: not-allowed;
            }
            .table select {
              border: 1px solid #e0e0e0;
              background: white;
              width: 100%;
              text-align: center;
              font-size: 13px;
              border-radius: 6px;
              padding: 8px 12px;
              transition: all 0.3s ease;
              padding-right: 35px;
            }
            .table select:focus {
              outline: none;
              border-color: #f75226;
              transform: translateY(-1px);
            }
            .status-field {
              background-color: #f8f9fa;
              border: 1px solid #dee2e6;
              padding: 6px 8px;
              font-size: 12px;
              text-align: center;
              min-width: 120px;
            }
          `}
        </style>

        {/* Main Content Area */}
        <div className="card mb-3" style={{ position: 'relative', zIndex: 1 }}>
          <div className="card-body" style={{ overflow: 'visible', position: 'relative', paddingBottom: '50px' }}>
            <div className="row">
              <div className="col-12">
                <div className="table-responsive" style={{ overflow: 'visible', position: 'relative', zIndex: 1 }}>
                  <div className="d-flex">
                    {/* Main Data Table */}
                    <div className="flex-grow-1" style={{ position: 'relative', zIndex: 1 }}>
                      <table className="table table-bordered mb-0" style={{ position: 'relative', zIndex: 1, marginBottom: '0' }}>
                        <thead>
                          <tr>
                            <th style={{ width: '5%' }}>SL No.</th>
                            <th style={{ width: '25%' }}>ITEMS</th>
                            <th style={{ width: '25%' }}>OTHER ITEMS</th>
                            <th style={{ width: '20%' }}>Closing</th>
                            {/* <th style={{ width: '25%' }}>Previous Year</th> */}
                            <th style={{ width: '20%' }}>Status</th>
                            <th style={{ width: '5%' }}>Action</th>
                          </tr>
                          <tr>
                            <th style={{ width: '5%' }}>(1)</th>
                            <th style={{ width: '25%' }}>(2)</th>
                            <th style={{ width: '25%' }}>(3)</th>
                            <th style={{ width: '20%' }}>(4)</th>
                            <th style={{ width: '20%' }}>(5)</th>
                            <th style={{ width: '5%' }}>(6)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {generateTableRows()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Remarks Section */}
        <div className='row'>
          <div className='col-md-6 remarks-section'>

            <Remarks
              remarksData={remarksData}
              setRemarksData={setRemarksData}
              blockItemData={profitLossItems}
              filteredBlockItems={filteredProfitLossItems}
              isLoadingBlockItems={isLoadingProfitLossItems}
              debouncedBlockItemSearch={debouncedBlockItemSearch}
              loadMoreBlockItemData={() => { }}
              hasMoreBlockItems={false}
              isLoadingMoreBlockItems={false}
              selectedItemsFromTable={formData.items.filter(item => item && item.trim() !== '')}
              remarksList={remarksList}
              availableColumns={[3, 4]}
            />

          </div>

          <div className='col-md-6'>
            <div className='card'>
              <div className='card-body'>
                <h6 className='mb-3' style={{ fontWeight: 'bold', color: '#f56565' }}><i className='fa fa-exclamation-triangle me-2'></i>Note</h6>
                <ul className='list-styled' style={{ fontSize: '14px', color: '#f56565' }}>
                  <li>Col-4 and 5 are Mandatory</li>
                  <li>For OTHERS item, Col-3 and Blocks are also Mandatory</li>

                </ul>
              </div>
            </div>
          </div>
        </div>


        {/* Action Buttons */}
        <div className="row">
          <div className="col-12">
            <hr />
          </div>
          <div className="col-md-8 text-center text-md-start mb-3 mb-md-0">
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => window.print()}
            >
              Print
              <i className="fa fa-print ms-1"></i>
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={handleSaveToAPI}
            >
              Save
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => {
                setFormData({
                  items: Array(100).fill(''),
                  otherItems: Array(100).fill(''),
                  closingData: Array(100).fill(''),
                  status: Array(100).fill('')
                });
                setVisibleRows(1);
                setDropdownStates({});
                setSearchTerms({});
              }}
            >
              Reset
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/asiScheduleMainMenu')}
            >
              Cancel
            </button>
          </div>
          <div className="col-md-4 text-center text-md-end">
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/balanceSheetData')}
            >
              <i className="fa fa-angle-left me-1"></i>
              Previous
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={async () => {
                const saved = await handleSaveToAPI();
                if (saved) {
                  navigate('/employmentAndLabourCost');
                }
              }}
            >
              Next
              <i className="fa fa-angle-right ms-1"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Remarks Lists Table */}
      <Remarks__list remarksData={filteredRemarksList} />
    </>
  );
}

export default ProfitAndLossAccount; 